<template>
  <div>
    <b-alert
      v-if="loadError"
      variant="danger"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ loadError }}
      </div>
    </b-alert>

    <!-- การ์ดสรุปคิดจาก place ทั้งหมดเสมอ ไม่ขึ้นกับตัวกรอง จึงใช้เป็นเมนูกรองได้ -->
    <b-row class="mb-1">
      <b-col
        v-for="card in cards"
        :key="card.key"
        md="2"
        sm="4"
        cols="6"
      >
        <b-card
          class="mb-1 stat-card"
          :class="{ 'stat-card-active': statusFilter === card.filter }"
          body-class="py-1"
          @click="card.filter !== undefined && applyStatus(card.filter)"
        >
          <h4 class="mb-0">
            {{ Number(card.value || 0).toLocaleString('en-US') }}
          </h4>
          <small class="text-muted">{{ card.label }}</small>
        </b-card>
      </b-col>
    </b-row>

    <b-card title="ตรวจพิกัด Place">
      <b-row class="mb-1">
        <b-col md="3">
          <b-form-group
            label="ค้นหา (ชื่อ / รหัส / id)"
            label-for="search"
          >
            <b-form-input
              id="search"
              v-model="search"
              placeholder="เช่น Beverly หรือ 3444"
              :disabled="isLoading"
              @keyup.enter="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="จังหวัด"
            label-for="province"
          >
            <b-form-select
              id="province"
              v-model="provinceFilter"
              :options="provinceOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="สถานะพิกัด"
            label-for="status"
          >
            <b-form-select
              id="status"
              v-model="statusFilter"
              :options="statusOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="3"
          class="d-flex align-items-end mb-1"
        >
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="isLoading"
            @click="onFilterChange"
          >
            ค้นหา
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="isLoading || isExporting"
            @click="exportCsv"
          >
            <b-spinner
              v-if="isExporting"
              small
              class="mr-50"
            />
            CSV
          </b-button>
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        responsive
        :items="places"
        :fields="fields"
        :busy="isLoading"
        show-empty
        empty-text="ไม่พบ place ที่ตรงเงื่อนไข"
      >
        <template #cell(place_name)="data">
          <span :title="data.item.place_name">{{ data.item.place_name || '—' }}</span>
        </template>

        <template #cell(province)="data">
          {{ data.item.province_th || '—' }}
        </template>

        <!-- คลิกเปิด Google Maps ตำแหน่งนั้นเพื่อดูด้วยตาว่าตรงจริงไหม -->
        <template #cell(coord)="data">
          <b-link
            v-if="hasCoord(data.item)"
            :href="mapUrl(data.item)"
            target="_blank"
            rel="noopener"
          >
            {{ Number(data.item.lat).toFixed(5) }}, {{ Number(data.item.lng).toFixed(5) }}
          </b-link>
          <span
            v-else
            class="text-danger"
          >ไม่มีพิกัด</span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
          <!-- บอกเหตุผลให้เห็นเลย จะได้ไม่ต้องเดาว่าทำไมถึงถูกตีว่าน่าสงสัย -->
          <small
            v-if="statusNote(data.item)"
            class="d-block text-muted"
          >{{ statusNote(data.item) }}</small>
        </template>

        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            :to="{ name: 'pin-edit', params: { id: data.item.place_id } }"
          >
            แก้ไข
          </b-button>
        </template>
      </b-table>

      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <small class="text-muted">
          พบ {{ Number(totalRows).toLocaleString('en-US') }} รายการ
          <span v-if="totalRows">· หน้า {{ currentPage }} จาก {{ totalPages }}</span>
        </small>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          class="mb-0"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <b-alert
      variant="secondary"
      show
    >
      <div class="alert-body">
        <small>
          <strong>ตรวจยังไง:</strong>
          <strong>นอกประเทศไทย</strong> = พิกัดหลุดขอบเขตไทย (มักเกิดจากระบบหาพิกัดไปเจอที่ชื่อซ้ำในต่างประเทศ) ·
          <strong>พิกัดซ้ำ</strong> = ใช้พิกัดเดียวกับ place อื่นเป๊ะ ๆ มักเป็นค่าที่ถูกเติมมาแบบ default ·
          <strong>หลุดโซน</strong> = อยู่ในไทยแต่ตกนอกขอบเขตโซนที่ผูกไว้ อาจผิดที่พิกัดหรือผิดที่โซนก็ได้ ·
          คลิกที่พิกัดเพื่อเปิด Google Maps ดูตำแหน่งจริง
        </small>
      </div>
    </b-alert>
  </div>
</template>

<script>
/*
 * ตรวจพิกัดของ place ทั้งหมด
 *
 * อ่านจาก POST /place/v1/coordinate-audit — เกณฑ์ตัดสินสถานะอยู่ฝั่ง API ที่เดียว
 * (model/place-coordinate-audit.js) เพื่อให้ตัวเลขบนการ์ด กับจำนวนแถวที่กรองได้ ตรงกันเสมอ
 */
import {
  BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
  BButton, BRow, BCol, BAlert, BPagination, BSpinner, BLink,
} from 'bootstrap-vue'
import axios from '@axios'

/* ดึงทีละก้อนตอน export ไม่งั้นเจ็ดพันกว่าแถวในครั้งเดียวจะช้าและเสี่ยง timeout */
const EXPORT_CHUNK = 1000

const STATUS = {
  ok: { label: 'ปกติ', variant: 'light-success' },
  missing: { label: 'ไม่มีพิกัด', variant: 'light-danger' },
  outside_th: { label: 'นอกประเทศไทย', variant: 'light-danger' },
  duplicate: { label: 'พิกัดซ้ำ', variant: 'light-warning' },
  zone_mismatch: { label: 'หลุดโซน', variant: 'light-warning' },
}

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BAlert,
    BPagination,
    BSpinner,
    BLink,
  },
  data() {
    return {
      places: [],
      summary: {},
      provinces: [],
      noProvinceCount: 0,
      fields: [
        { key: 'place_id', label: 'ID' },
        { key: 'place_name', label: 'ชื่อ Place' },
        { key: 'province', label: 'จังหวัด' },
        { key: 'coord', label: 'พิกัด (lat, lng)' },
        { key: 'status', label: 'สถานะ' },
        { key: 'actions', label: '' },
      ],
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      isLoading: false,
      isExporting: false,
      loadError: '',
      search: '',
      provinceFilter: '',
      statusFilter: '',
      statusOptions: [
        { value: '', text: 'ทั้งหมด' },
        { value: 'ok', text: 'ปกติ' },
        { value: 'outside_th', text: 'นอกประเทศไทย' },
        { value: 'duplicate', text: 'พิกัดซ้ำ' },
        { value: 'zone_mismatch', text: 'หลุดโซน' },
        { value: 'missing', text: 'ไม่มีพิกัด' },
      ],
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage))
    },
    provinceOptions() {
      return [
        { value: '', text: 'ทุกจังหวัด' },
        { value: 'none', text: `ไม่ระบุจังหวัด (${this.noProvinceCount})` },
        ...this.provinces.map(p => ({
          value: p.id,
          text: `${p.name_th} (${p.places})`,
        })),
      ]
    },
    cards() {
      return [
        { key: 'total', label: 'ทั้งหมด', value: this.summary.total },
        {
          key: 'ok', label: 'ปกติ', value: this.summary.ok, filter: 'ok',
        },
        {
          key: 'outside_th', label: 'นอกไทย', value: this.summary.outside_th, filter: 'outside_th',
        },
        {
          key: 'duplicate', label: 'พิกัดซ้ำ', value: this.summary.duplicate, filter: 'duplicate',
        },
        {
          key: 'zone_mismatch', label: 'หลุดโซน', value: this.summary.zone_mismatch, filter: 'zone_mismatch',
        },
        {
          key: 'missing', label: 'ไม่มีพิกัด', value: this.summary.missing, filter: 'missing',
        },
      ]
    },
    /* พารามิเตอร์ชุดเดียวกันทั้งตอนแสดงและตอน export ผลจะได้ตรงกันเสมอ */
    filterParams() {
      return {
        ...(this.search ? { search: this.search } : {}),
        ...(this.provinceFilter !== '' ? { province_id: this.provinceFilter } : {}),
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
      }
    },
  },
  mounted() {
    this.fetchProvinces()
    this.fetchPlaces()
  },
  methods: {
    hasCoord(item) {
      return Number(item.lat) !== 0 && Number(item.lng) !== 0 && item.lat !== null
    },
    mapUrl(item) {
      return `https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lng}`
    },
    statusLabel(status) {
      return (STATUS[status] || {}).label || status
    },
    statusVariant(status) {
      return (STATUS[status] || {}).variant || 'light-secondary'
    },
    statusNote(item) {
      if (item.status === 'duplicate') return `ใช้พิกัดนี้ร่วมกับอีก ${Number(item.same_coord_count) - 1} แห่ง`
      if (item.status === 'zone_mismatch') return `ผูกไว้ ${item.zone_count} โซน แต่ไม่ตกอยู่ในโซนใดเลย`

      return ''
    },

    async fetchProvinces() {
      try {
        const response = await axios.get('place/v1/provinces')

        this.provinces = response?.data?.data || []
        this.noProvinceCount = Number(response?.data?.no_province || 0)
      } catch (error) {
        // ไม่ได้รายชื่อจังหวัดก็ยังใช้หน้าได้ แค่กรองตามจังหวัดไม่ได้
        console.error('Error loading provinces:', error)
      }
    },

    async fetchPlaces() {
      try {
        this.isLoading = true
        this.loadError = ''

        const response = await axios.post('place/v1/coordinate-audit', {
          perpage: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...this.filterParams,
        })

        this.places = response?.data?.data || []
        this.totalRows = Number(response?.data?.total || 0)
        this.summary = response?.data?.summary || {}
      } catch (error) {
        this.places = []
        this.totalRows = 0
        this.loadError = `โหลดข้อมูลไม่สำเร็จ${error?.response?.status ? ` (HTTP ${error.response.status})` : ''}`
        console.error('Error loading place coordinate audit:', error)
      } finally {
        this.isLoading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.fetchPlaces()
    },

    /*
     * b-pagination ส่งเลขหน้าใหม่มาทาง argument แต่ยังไม่อัปเดต v-model ตอนที่ยิง event
     * ถ้าอ่านจาก this.currentPage จะได้หน้าเก่าไปหนึ่งก้าวเสมอ
     */
    onPageChange(page) {
      this.currentPage = page

      return this.$nextTick(this.fetchPlaces)
    },

    applyStatus(status) {
      this.statusFilter = status

      return this.onFilterChange()
    },

    /* ดาวน์โหลดตามตัวกรองที่เลือกอยู่ ไม่ใช่แค่หน้าที่เห็น */
    async exportCsv() {
      try {
        this.isExporting = true
        this.loadError = ''

        const rows = []
        for (let offset = 0; ; offset += EXPORT_CHUNK) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.post('place/v1/coordinate-audit', {
            perpage: EXPORT_CHUNK,
            offset,
            ...this.filterParams,
          })
          const chunk = response?.data?.data || []

          rows.push(...chunk)
          if (chunk.length < EXPORT_CHUNK) break
        }

        this.downloadCsv(rows)
      } catch (error) {
        this.loadError = 'ดาวน์โหลด CSV ไม่สำเร็จ'
        console.error('Error exporting place coordinate audit:', error)
      } finally {
        this.isExporting = false
      }
    },

    downloadCsv(rows) {
      const header = ['place_id', 'place_name', 'code', 'province_th', 'province_en',
        'lat', 'lng', 'status', 'same_coord_count', 'zone_count', 'zone_inside']

      /* RFC4180: ครอบด้วย " เมื่อมี , " หรือขึ้นบรรทัดใหม่ แล้ว escape " เป็น "" */
      const cell = v => {
        if (v === null || v === undefined) return ''
        const s = String(v)

        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }

      const csv = [
        header.join(','),
        ...rows.map(r => header.map(h => cell(r[h])).join(',')),
      ].join('\n')

      // \uFEFF = BOM ให้ Excel อ่านภาษาไทยไม่เป็นตัวยึกยือ
      // เขียนเป็น escape ไม่ใช่อักขระจริง ไม่งั้นมันมองไม่เห็นในซอร์สและ eslint ก็ฟ้อง
      const blob = new Blob([`\uFEFF${csv}\n`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = `place-coordinate-audit-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.stat-card {
  cursor: pointer;
  transition: border-color 0.15s ease;
  border: 1px solid transparent;
}

.stat-card-active {
  border-color: #7367f0;
}
</style>
